import { useEffect, useMemo, useState } from 'react';
import { IconCheck, IconInfo, IconRefresh } from './icons.jsx';
import { Button, Pill } from './primitives.jsx';
import { PALETTE } from '../palette.js';

const styles = `
  .funnel-readiness {
    border-block: 1px solid ${PALETTE.line};
    background: ${PALETTE.surface};
    margin: 0 0 20px;
  }
  .funnel-readiness-header {
    display: flex;
    align-items: flex-start;
    gap: 16px;
    padding: 18px 20px;
  }
  .funnel-readiness-layout {
    display: grid;
    grid-template-columns: minmax(260px, .72fr) minmax(0, 1.28fr);
    border-top: 1px solid ${PALETTE.line};
  }
  .funnel-readiness-list {
    border-right: 1px solid ${PALETTE.line};
    padding: 8px 0;
  }
  .funnel-readiness-row {
    display: grid;
    grid-template-columns: 20px minmax(0, 1fr) auto;
    gap: 10px;
    align-items: start;
    padding: 11px 18px;
    border-bottom: 1px solid ${PALETTE.lineSoft};
  }
  .funnel-readiness-row:last-child { border-bottom: 0; }
  .funnel-readiness-editor { padding: 18px 20px 22px; min-width: 0; }
  .funnel-readiness-fields {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px 16px;
  }
  .funnel-readiness-field { display: grid; gap: 6px; min-width: 0; }
  .funnel-readiness-field-wide { grid-column: 1 / -1; }
  .funnel-readiness-input {
    width: 100%;
    box-sizing: border-box;
    min-height: 42px;
    border: 1px solid ${PALETTE.line};
    border-radius: 6px;
    background: ${PALETTE.surface};
    color: ${PALETTE.ink};
    padding: 9px 11px;
    font: 400 13px/1.4 Inter, sans-serif;
  }
  textarea.funnel-readiness-input { min-height: 88px; resize: vertical; }
  .funnel-readiness-mark-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
  }
  .funnel-readiness-mark {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    min-height: 44px;
    padding: 10px;
    border: 1px solid ${PALETTE.line};
    border-radius: 6px;
    cursor: pointer;
  }
  .funnel-readiness-mark:has(input:checked) {
    border-color: ${PALETTE.accent};
    background: ${PALETTE.accentTint};
  }
  .funnel-readiness-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-top: 18px;
    padding-top: 16px;
    border-top: 1px solid ${PALETTE.line};
  }
  @media (max-width: 900px) {
    .funnel-readiness-layout { grid-template-columns: minmax(0, 1fr); }
    .funnel-readiness-list { border-right: 0; border-bottom: 1px solid ${PALETTE.line}; }
  }
  @media (max-width: 620px) {
    .funnel-readiness-header { display: grid; padding: 16px; }
    .funnel-readiness-fields { grid-template-columns: minmax(0, 1fr); }
    .funnel-readiness-field-wide { grid-column: auto; }
    .funnel-readiness-mark-list { grid-template-columns: minmax(0, 1fr); }
    .funnel-readiness-editor { padding: 16px; }
    .funnel-readiness-actions { align-items: stretch; flex-direction: column; }
    .funnel-readiness-actions button { justify-content: center; width: 100%; }
  }
`;

function text(value) {
  return typeof value === 'string' ? value : '';
}

function lines(value) {
  return Array.isArray(value) ? value.join('\n') : '';
}

function colors(value) {
  return Array.isArray(value) ? value.map((item) => item?.hex).filter(Boolean).join(', ') : '';
}

function socialLines(value) {
  return Array.isArray(value)
    ? value.map((item) => `${item?.platform || 'Social'} | ${item?.url || ''}`).join('\n')
    : '';
}

function initialForm(completion) {
  const fields = completion?.fields || {};
  const mark = completion?.selectedBrandMark;
  return {
    displayName: text(fields.displayName?.value),
    phone: text(fields.phone?.value),
    email: text(fields.email?.value),
    address: text(fields.address?.value),
    services: lines(fields.services?.value),
    colors: colors(fields.colors?.value),
    socialLinks: socialLines(fields.socialLinks?.value),
    serviceArea: lines(fields.serviceArea?.value),
    country: text(fields.country?.value) || 'CA',
    ctaLabel: text(fields.ctaLabel?.value),
    consentLabel: text(fields.consentLabel?.value),
    bookingEnabled: fields.bookingEnabled?.value === true,
    brandMark: mark?.type === 'logo' ? `logo:${mark.artifactId}` : 'text',
    textMark: mark?.type === 'text'
      ? text(mark.text)
      : text(fields.displayName?.value),
  };
}

function lineArray(value) {
  return [...new Set(text(value).split(/\r?\n/).map((item) => item.trim()).filter(Boolean))];
}

function colorArray(value) {
  return [...new Set(text(value).split(/[\s,]+/).map((item) => item.trim().toLowerCase()).filter(Boolean))]
    .map((hex, index) => ({ name: `Brand color ${index + 1}`, hex }));
}

function socialArray(value) {
  return lineArray(value).map((item) => {
    const [platform, ...urlParts] = item.split('|');
    return {
      platform: text(platform).trim(),
      url: urlParts.join('|').trim(),
    };
  });
}

export function funnelCompletionSubmission(form, baseVersionId) {
  const logoId = form.brandMark.startsWith('logo:') ? form.brandMark.slice(5) : '';
  return {
    baseVersionId,
    fields: {
      displayName: form.displayName,
      phone: form.phone,
      email: form.email,
      address: form.address,
      services: lineArray(form.services),
      colors: colorArray(form.colors),
      socialLinks: socialArray(form.socialLinks),
      serviceArea: lineArray(form.serviceArea),
      country: form.country,
      ctaLabel: form.ctaLabel,
      consentLabel: form.consentLabel,
      bookingEnabled: form.bookingEnabled,
    },
    brandMark: logoId
      ? { type: 'logo', artifactId: logoId }
      : { type: 'text', text: form.textMark || form.displayName },
  };
}

function sourceLabel(field) {
  const source = text(field?.source).replaceAll('_', ' ');
  const confidence = text(field?.confidence).replaceAll('_', ' ');
  return [source, confidence].filter(Boolean).join(' / ') || 'not available';
}

function toneForStatus(status) {
  if (status === 'ready') return 'live';
  if (status === 'missing' || status === 'unverified') return 'warn';
  return 'neutral';
}

function readinessRows(completion) {
  const manifest = completion?.readiness || {};
  return [
    ...(manifest.required || []).map((item) => ({ ...item, group: 'Required' })),
    ...(manifest.conditional || []).map((item) => ({ ...item, group: 'Conditional' })),
    ...(manifest.optional || []).map((item) => ({ ...item, group: 'Optional' })),
  ];
}

function Field({
  completion,
  form,
  setForm,
  name,
  label,
  required = false,
  wide = false,
  disabled = false,
  children,
}) {
  const candidate = completion?.fields?.[name];
  return (
    <label className={`funnel-readiness-field${wide ? ' funnel-readiness-field-wide' : ''}`}>
      <span style={fieldLabelStyle}>{label}{required ? ' *' : ''}</span>
      {children || (
        <input
          className="funnel-readiness-input"
          value={form[name]}
          onChange={(event) => setForm((current) => ({ ...current, [name]: event.target.value }))}
          disabled={disabled}
        />
      )}
      <span style={sourceStyle}>{sourceLabel(candidate)}</span>
    </label>
  );
}

export default function FunnelReadinessPanel({
  completion,
  selectedVersionId,
  canManage,
  busy = false,
  message = '',
  onComplete,
  onRefreshResearch,
}) {
  const [form, setForm] = useState(() => initialForm(completion));
  const rows = useMemo(() => readinessRows(completion), [completion?.readiness]);

  useEffect(() => {
    setForm(initialForm(completion));
  }, [selectedVersionId, completion?.research?.id]);

  if (!completion || !selectedVersionId) return null;
  const readiness = completion.readiness;
  const ready = readiness?.status === 'ready';
  const logos = (completion.brandMarks || []).filter((mark) => mark.type === 'logo');

  return (
    <section className="funnel-readiness" aria-labelledby="funnel-readiness-title">
      <style>{styles}</style>
      <header className="funnel-readiness-header">
        <div style={{ minWidth: 0 }}>
          <div style={eyebrowStyle}>Funnel completion</div>
          <h2 id="funnel-readiness-title" style={titleStyle}>
            {ready ? 'Exact version ready for approval' : 'Complete the reviewable draft'}
          </h2>
          <p style={bodyStyle}>
            Website findings are candidates until an owner or admin confirms them. Missing optional details do not block approval.
          </p>
        </div>
        <div style={{ flex: 1 }} />
        <Pill tone={ready ? 'live' : 'warn'}>{ready ? 'Ready' : 'Action required'}</Pill>
        {canManage && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onRefreshResearch}
            disabled={busy || completion.research?.status === 'unavailable'}
            title="Refresh website research candidates"
          >
            <IconRefresh size={14} /> Refresh research
          </Button>
        )}
      </header>

      <div className="funnel-readiness-layout">
        <div className="funnel-readiness-list" aria-label="Funnel readiness">
          {rows.map((item) => (
            <div className="funnel-readiness-row" key={`${item.group}:${item.id}`}>
              <span style={{
                color: item.status === 'ready' ? PALETTE.success : PALETTE.warn,
                marginTop: 1,
              }}>
                {item.status === 'ready' ? <IconCheck size={16} /> : <IconInfo size={16} />}
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 12.5, fontWeight: 600, color: PALETTE.ink }}>{item.label}</div>
                {item.message && <div style={{ fontSize: 11.5, lineHeight: 1.45, color: PALETTE.ink3, marginTop: 3 }}>{item.message}</div>}
              </div>
              <Pill tone={toneForStatus(item.status)} style={{ fontSize: 9, padding: '3px 7px' }}>
                {item.group}
              </Pill>
            </div>
          ))}
        </div>

        <form
          className="funnel-readiness-editor"
          onSubmit={(event) => {
            event.preventDefault();
            if (!canManage || busy) return;
            onComplete?.(funnelCompletionSubmission(form, selectedVersionId));
          }}
        >
          <div className="funnel-readiness-fields">
            <Field completion={completion} form={form} setForm={setForm} name="displayName" label="Business display name" required disabled={!canManage || busy} />
            <Field completion={completion} form={form} setForm={setForm} name="serviceArea" label="Service area, one per line" required disabled={!canManage || busy}>
              <textarea className="funnel-readiness-input" value={form.serviceArea} onChange={(event) => setForm((current) => ({ ...current, serviceArea: event.target.value }))} disabled={!canManage || busy} />
            </Field>
            <Field completion={completion} form={form} setForm={setForm} name="phone" label="Business phone" disabled={!canManage || busy} />
            <Field completion={completion} form={form} setForm={setForm} name="email" label="Business email" disabled={!canManage || busy} />
            <Field completion={completion} form={form} setForm={setForm} name="address" label="Business address" wide disabled={!canManage || busy} />
            <Field completion={completion} form={form} setForm={setForm} name="services" label="Services, one per line" disabled={!canManage || busy}>
              <textarea className="funnel-readiness-input" value={form.services} onChange={(event) => setForm((current) => ({ ...current, services: event.target.value }))} disabled={!canManage || busy} />
            </Field>
            <Field completion={completion} form={form} setForm={setForm} name="colors" label="Brand colors, comma separated" disabled={!canManage || busy} />
            <Field completion={completion} form={form} setForm={setForm} name="socialLinks" label="Social links: Platform | https://..." wide disabled={!canManage || busy}>
              <textarea className="funnel-readiness-input" value={form.socialLinks} onChange={(event) => setForm((current) => ({ ...current, socialLinks: event.target.value }))} disabled={!canManage || busy} />
            </Field>
            <Field completion={completion} form={form} setForm={setForm} name="country" label="Campaign country" required disabled={!canManage || busy}>
              <select className="funnel-readiness-input" value={form.country} onChange={(event) => setForm((current) => ({ ...current, country: event.target.value }))} disabled={!canManage || busy}>
                <option value="CA">Canada</option>
                <option value="US">United States</option>
                <option value="MX">Mexico</option>
              </select>
            </Field>
            <Field completion={completion} form={form} setForm={setForm} name="ctaLabel" label="Primary call to action" required disabled={!canManage || busy} />
            <Field completion={completion} form={form} setForm={setForm} name="consentLabel" label="Consent and legal copy" required wide disabled={!canManage || busy}>
              <textarea className="funnel-readiness-input" value={form.consentLabel} onChange={(event) => setForm((current) => ({ ...current, consentLabel: event.target.value }))} disabled={!canManage || busy} />
            </Field>
            <div className="funnel-readiness-field funnel-readiness-field-wide">
              <span style={fieldLabelStyle}>Brand mark *</span>
              <div className="funnel-readiness-mark-list">
                {logos.map((mark) => (
                  <label className="funnel-readiness-mark" key={mark.artifactId}>
                    <input
                      type="radio"
                      name="brand-mark"
                      value={`logo:${mark.artifactId}`}
                      checked={form.brandMark === `logo:${mark.artifactId}`}
                      onChange={(event) => setForm((current) => ({ ...current, brandMark: event.target.value }))}
                      disabled={!canManage || busy}
                    />
                    <span>
                      <strong style={{ display: 'block', fontSize: 12.5 }}>Approved logo</strong>
                      <span style={sourceStyle}>{mark.label} / {mark.source}</span>
                    </span>
                  </label>
                ))}
                <label className="funnel-readiness-mark">
                  <input
                    type="radio"
                    name="brand-mark"
                    value="text"
                    checked={form.brandMark === 'text'}
                    onChange={(event) => setForm((current) => ({ ...current, brandMark: event.target.value }))}
                    disabled={!canManage || busy}
                  />
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <strong style={{ display: 'block', fontSize: 12.5 }}>Approved text mark</strong>
                    <input
                      className="funnel-readiness-input"
                      value={form.textMark}
                      onChange={(event) => setForm((current) => ({ ...current, textMark: event.target.value, brandMark: 'text' }))}
                      disabled={!canManage || busy}
                      style={{ marginTop: 7 }}
                    />
                  </span>
                </label>
              </div>
            </div>
            <label className="funnel-readiness-field funnel-readiness-field-wide" style={{ display: 'flex', gridTemplateColumns: 'auto minmax(0, 1fr)', alignItems: 'center' }}>
              <input
                type="checkbox"
                checked={form.bookingEnabled}
                onChange={(event) => setForm((current) => ({ ...current, bookingEnabled: event.target.checked }))}
                disabled={!canManage || busy}
              />
              <span>
                <strong style={{ display: 'block', fontSize: 12.5, color: PALETTE.ink }}>Enable online booking</strong>
                <span style={sourceStyle}>
                  Booking becomes a required approval condition only when enabled.
                  {completion.booking?.ready ? ' A provider is connected.' : ' No provider is connected.'}
                </span>
              </span>
            </label>
          </div>
          <div className="funnel-readiness-actions">
            <span role="status" style={{ fontSize: 12, lineHeight: 1.45, color: message ? PALETTE.ink2 : PALETTE.ink3 }}>
              {message || (canManage ? 'Saving creates a new immutable review version.' : 'Owner or admin access is required to confirm values.')}
            </span>
            {canManage && (
              <Button variant="accent" type="submit" disabled={busy}>
                <IconCheck size={14} /> {busy ? 'Saving...' : 'Confirm and create version'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}

const eyebrowStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 10,
  textTransform: 'uppercase',
  color: PALETTE.accent,
  marginBottom: 6,
};
const titleStyle = {
  margin: 0,
  color: PALETTE.ink,
  fontFamily: 'Inter, sans-serif',
  fontSize: 19,
  lineHeight: 1.25,
};
const bodyStyle = {
  maxWidth: 760,
  margin: '6px 0 0',
  color: PALETTE.ink3,
  fontSize: 12.5,
  lineHeight: 1.5,
};
const fieldLabelStyle = {
  fontFamily: 'JetBrains Mono, monospace',
  fontSize: 9.5,
  textTransform: 'uppercase',
  color: PALETTE.ink3,
};
const sourceStyle = {
  display: 'block',
  color: PALETTE.ink3,
  fontSize: 10.5,
  lineHeight: 1.4,
};
